import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Plus, 
  AlertTriangle, 
  HelpCircle, 
  AlertCircle, 
  Link2,
  Edit,
  Trash2,
  Loader2,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const RAIDLog = ({ projectId, canEdit = false, users = [] }) => {
  const [raidItems, setRaidItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'Risk',
    description: '',
    status: 'Open',
    likelihood: '',
    impact: '',
    owner_id: '',
    mitigation: '',
    due_date: ''
  });

  useEffect(() => {
    fetchRAIDItems();
  }, [projectId]);

  const fetchRAIDItems = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/raid`);
      setRaidItems(response.data);
    } catch (error) {
      console.error('Failed to fetch RAID items');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'Risk',
      description: '',
      status: 'Open',
      likelihood: '',
      impact: '',
      owner_id: '',
      mitigation: '',
      due_date: ''
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setFormData({
        type: item.type,
        description: item.description,
        status: item.status || 'Open',
        likelihood: item.likelihood || '',
        impact: item.impact || '',
        owner_id: item.owner_id || '',
        mitigation: item.mitigation || '',
        due_date: item.due_date || ''
      });
      setEditingItem(item);
    } else {
      resetForm();
    }
    setShowAddDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingItem) {
        await api.put(`/projects/${projectId}/raid/${editingItem.id}`, formData);
        toast.success(`${formData.type} updated`);
      } else {
        await api.post(`/projects/${projectId}/raid`, formData);
        toast.success(`${formData.type} added`);
      }
      setShowAddDialog(false);
      resetForm();
      fetchRAIDItems();
    } catch (error) {
      toast.error(`Failed to ${editingItem ? 'update' : 'add'} ${formData.type.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/projects/${projectId}/raid/${itemId}`);
      toast.success('Item deleted');
      fetchRAIDItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Risk':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'Assumption':
        return <HelpCircle className="w-4 h-4 text-blue-500" />;
      case 'Issue':
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case 'Dependency':
        return <Link2 className="w-4 h-4 text-purple-500" />;
      default:
        return <Shield className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'bg-red-100 text-red-800';
      case 'Mitigated':
        return 'bg-amber-100 text-amber-800';
      case 'Closed':
      case 'Resolved':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getSeverityColor = (level) => {
    switch (level) {
      case 'High':
        return 'text-red-600 bg-red-50';
      case 'Medium':
        return 'text-amber-600 bg-amber-50';
      case 'Low':
        return 'text-emerald-600 bg-emerald-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };

  const filteredItems = activeTab === 'all' 
    ? raidItems 
    : raidItems.filter(item => item.type === activeTab);

  const counts = {
    all: raidItems.length,
    Risk: raidItems.filter(i => i.type === 'Risk').length,
    Assumption: raidItems.filter(i => i.type === 'Assumption').length,
    Issue: raidItems.filter(i => i.type === 'Issue').length,
    Dependency: raidItems.filter(i => i.type === 'Dependency').length
  };

  const openRisks = raidItems.filter(i => i.type === 'Risk' && i.status === 'Open').length;
  const openIssues = raidItems.filter(i => i.type === 'Issue' && i.status === 'Open').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <Card data-testid="raid-log-section">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#235894]" />
            RAID Log
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Risks, Assumptions, Issues & Dependencies
          </p>
        </div>
        {canEdit && (
          <Button 
            size="sm" 
            onClick={() => handleOpenDialog()}
            data-testid="add-raid-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Item
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        {raidItems.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <div className={cn(
              "p-2 rounded-lg text-center",
              openRisks > 0 ? "bg-red-50" : "bg-slate-50"
            )}>
              <p className={cn(
                "text-lg font-bold",
                openRisks > 0 ? "text-red-600" : "text-slate-600"
              )}>
                {openRisks}
              </p>
              <p className="text-xs text-slate-500">Open Risks</p>
            </div>
            <div className={cn(
              "p-2 rounded-lg text-center",
              openIssues > 0 ? "bg-amber-50" : "bg-slate-50"
            )}>
              <p className={cn(
                "text-lg font-bold",
                openIssues > 0 ? "text-amber-600" : "text-slate-600"
              )}>
                {openIssues}
              </p>
              <p className="text-xs text-slate-500">Open Issues</p>
            </div>
            <div className="p-2 rounded-lg bg-slate-50 text-center">
              <p className="text-lg font-bold text-blue-600">{counts.Assumption}</p>
              <p className="text-xs text-slate-500">Assumptions</p>
            </div>
            <div className="p-2 rounded-lg bg-slate-50 text-center">
              <p className="text-lg font-bold text-purple-600">{counts.Dependency}</p>
              <p className="text-xs text-slate-500">Dependencies</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-5 mb-4">
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="Risk">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {counts.Risk}
            </TabsTrigger>
            <TabsTrigger value="Assumption">
              <HelpCircle className="w-3 h-3 mr-1" />
              {counts.Assumption}
            </TabsTrigger>
            <TabsTrigger value="Issue">
              <AlertCircle className="w-3 h-3 mr-1" />
              {counts.Issue}
            </TabsTrigger>
            <TabsTrigger value="Dependency">
              <Link2 className="w-3 h-3 mr-1" />
              {counts.Dependency}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-0">
            {filteredItems.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Shield className="w-8 h-8 mx-auto mb-2" />
                <p>No {activeTab === 'all' ? 'RAID items' : activeTab.toLowerCase() + 's'} yet</p>
                {canEdit && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => handleOpenDialog()}
                  >
                    Add your first item
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredItems.map((item) => (
                  <div 
                    key={item.id}
                    className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`raid-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        {getTypeIcon(item.type)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className="text-xs">
                              {item.type}
                            </Badge>
                            <Badge className={cn("text-xs", getStatusColor(item.status))}>
                              {item.status}
                            </Badge>
                            {item.likelihood && (
                              <span className={cn(
                                "text-xs px-2 py-0.5 rounded",
                                getSeverityColor(item.likelihood)
                              )}>
                                L: {item.likelihood}
                              </span>
                            )}
                            {item.impact && (
                              <span className={cn(
                                "text-xs px-2 py-0.5 rounded",
                                getSeverityColor(item.impact)
                              )}>
                                I: {item.impact}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-900">{item.description}</p>
                          {item.mitigation && (
                            <p className="text-xs text-slate-500 mt-1">
                              <strong>Mitigation:</strong> {item.mitigation}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            {item.owner_name && (
                              <span>Owner: {item.owner_name}</span>
                            )}
                            {item.due_date && (
                              <span>Due: {item.due_date}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleOpenDialog(item)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? `Edit ${formData.type}` : 'Add RAID Item'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the details below' : 'Add a new Risk, Assumption, Issue, or Dependency'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger data-testid="raid-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Risk">Risk</SelectItem>
                    <SelectItem value="Assumption">Assumption</SelectItem>
                    <SelectItem value="Issue">Issue</SelectItem>
                    <SelectItem value="Dependency">Dependency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger data-testid="raid-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Mitigated">Mitigated</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                    <SelectItem value="Resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the risk, assumption, issue, or dependency..."
                rows={3}
                data-testid="raid-description-input"
              />
            </div>

            {(formData.type === 'Risk' || formData.type === 'Issue') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Likelihood</Label>
                  <Select 
                    value={formData.likelihood} 
                    onValueChange={(v) => setFormData({ ...formData, likelihood: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Impact</Label>
                  <Select 
                    value={formData.impact} 
                    onValueChange={(v) => setFormData({ ...formData, impact: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Mitigation / Response Plan</Label>
              <Textarea
                value={formData.mitigation}
                onChange={(e) => setFormData({ ...formData, mitigation: e.target.value })}
                placeholder="How will this be addressed?"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Owner</Label>
                <Select 
                  value={formData.owner_id} 
                  onValueChange={(v) => setFormData({ ...formData, owner_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Assign owner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !formData.description.trim()}
              className="bg-[#235894] hover:bg-[#1E4B80]"
              data-testid="save-raid-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingItem ? 'Update' : 'Add'} {formData.type}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RAIDLog;
